"use client"

import { useMemo, useState } from 'react'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { SmartAnalysisSegmentedOutcomeBar } from '@/components/search/smart-analysis-segmented-outcome-bar'
import type { ApplicantCard } from '@/hooks/use-opportunity-applicants'
import type { JobAnalysisCoverage } from '@/hooks/use-job-analysis-coverage'
import { isActiveApplicationStatus } from '@/utils/application-status'
import { matchBandCountsFromApplicants } from '@/lib/smart-analysis-match-quality'
import { employerScoreboardTileLabel } from '@/lib/ranking-vocabulary'
import { MATCH_BANDS, type MatchBand } from '@/lib/match-bands'
import { cn } from '@/lib/utils'

export type SmartAnalysisScoreboardProps = {
  applicants: ApplicantCard[]
  coverage?: JobAnalysisCoverage | null
  /** Prose run summary merged into the match quality card (job report). */
  summary?: string | null
  llmJudgedCount?: number | null
  /** Talent pipeline counts vs Data bench scored-pool counts. */
  metaMode?: 'pipeline' | 'pool'
  className?: string
}

function scoreboardTileTone(
  band: MatchBand,
): 'success' | 'primary' | 'neutral' | 'warning' | 'destructive' {
  if (band === 'strong') return 'success'
  if (band === 'good') return 'primary'
  if (band === 'mixed') return 'neutral'
  if (band === 'limited') return 'warning'
  return 'destructive'
}

function StatTile({
  label,
  value,
  tone,
}: {
  label: string
  value: number
  tone: 'success' | 'primary' | 'neutral' | 'warning' | 'destructive'
}) {
  return (
    <div
      className={cn(
        'rounded-lg border px-3 py-2',
        tone === 'success' && 'border-success/30 bg-success/10 text-success',
        tone === 'primary' && 'border-primary/30 bg-primary/10 text-primary',
        tone === 'neutral' && 'border-border bg-muted/60 text-foreground',
        tone === 'warning' && 'border-warning/30 bg-warning/10 text-warning',
        tone === 'destructive' && 'border-destructive/30 bg-destructive/10 text-destructive',
      )}
    >
      <p className="text-2xl font-semibold tabular-nums">{value}</p>
      <p className="text-xs font-medium">{label}</p>
    </div>
  )
}

function MatchQualityPanel({
  applicants,
  summary,
  llmJudgedCount,
  metaMode = 'pipeline',
}: {
  applicants: ApplicantCard[]
  summary?: string | null
  llmJudgedCount?: number | null
  metaMode?: 'pipeline' | 'pool'
}) {
  const activeApplicants = applicants.filter((applicant) =>
    isActiveApplicationStatus(applicant.status),
  )
  const bandCounts = matchBandCountsFromApplicants(activeApplicants)
  let appliedCount = 0
  let invitedCount = 0
  let unanalysedCount = 0
  for (const applicant of activeApplicants) {
    if (applicant.status === 'invited') invitedCount += 1
    else appliedCount += 1
    if (applicant.match_score == null) unanalysedCount += 1
  }
  const summaryText = summary?.trim()

  const scoredCount = activeApplicants.filter((applicant) => applicant.match_score != null).length
  const metaParts =
    metaMode === 'pool'
      ? [`${scoredCount} scored`, `${activeApplicants.length} in pool`]
      : [`${appliedCount} applied`, `${invitedCount} invited`]
  if (metaMode !== 'pool' && unanalysedCount > 0) {
    metaParts.push(`${unanalysedCount} not yet analysed`)
  }
  if (llmJudgedCount != null && llmJudgedCount > 0) {
    metaParts.push(`${llmJudgedCount} LLM evaluated`)
  }

  return (
    <Card className="h-full gap-0 p-0">
      <CardHeader className="border-b border-border/60 px-[var(--sp-card)] py-3">
        <CardTitle className="text-sm font-semibold">Analysis summary</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 px-[var(--sp-card)] py-4">
        {summaryText ? <p className="text-sm text-foreground">{summaryText}</p> : null}
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
          {MATCH_BANDS.map((band) => (
            <StatTile
              key={band}
              label={employerScoreboardTileLabel(band)}
              value={bandCounts[band]}
              tone={scoreboardTileTone(band)}
            />
          ))}
        </div>
        <SmartAnalysisSegmentedOutcomeBar
          counts={{
            // Approximate tier buckets from the shared 5-band map (tiers stay a separate product concept).
            top: bandCounts.strong,
            compelling: bandCounts.good + bandCounts.mixed,
            precluded: bandCounts.limited + bandCounts.poor,
          }}
        />
        <p className="text-sm text-muted-foreground">{metaParts.join(' · ')}</p>
      </CardContent>
    </Card>
  )
}

const COVERAGE_VISIBLE_ROWS = 6

function RequirementCoveragePanel({ coverage }: { coverage: JobAnalysisCoverage }) {
  const [showAll, setShowAll] = useState(false)
  const sortedRequirements = useMemo(
    () =>
      [...coverage.requirements].sort((a, b) => {
        const ratioA = a.total > 0 ? a.covered / a.total : 0
        const ratioB = b.total > 0 ? b.covered / b.total : 0
        return ratioB - ratioA
      }),
    [coverage.requirements],
  )
  const hasOverflow = sortedRequirements.length > COVERAGE_VISIBLE_ROWS
  const visibleRequirements = showAll
    ? sortedRequirements
    : sortedRequirements.slice(0, COVERAGE_VISIBLE_ROWS)

  return (
    <Card className="h-full gap-0 p-0">
      <CardHeader className="border-b border-border/60 px-[var(--sp-card)] py-3">
        <CardTitle className="text-sm font-semibold">Requirement coverage</CardTitle>
        <p className="text-xs font-normal text-muted-foreground">
          Based on profile skills — not analysis evidence.
        </p>
      </CardHeader>
      <CardContent className="space-y-3 px-[var(--sp-card)] py-4">
        {coverage.requirements.length === 0 ? (
          <p className="text-sm text-muted-foreground">No skill requirements configured for this job.</p>
        ) : (
          visibleRequirements.map((requirement) => {
            const ratio =
              requirement.total > 0 ? requirement.covered / requirement.total : 0
            return (
              <div key={requirement.label} className="space-y-1.5">
                <div className="flex items-center justify-between gap-2 text-sm">
                  <span className="min-w-0 truncate font-medium text-foreground">
                    {requirement.label}
                  </span>
                  <span className="shrink-0 tabular-nums text-muted-foreground">
                    {requirement.covered}/{requirement.total}
                  </span>
                </div>
                {/* Neutral fill (matches evaluation-dimension-row): coverage is
                    descriptive pool data, not a health check — bar length alone
                    carries the information. */}
                <div className="h-2 overflow-hidden rounded-full bg-muted">
                  <div
                    className="h-full rounded-full bg-primary/70 transition-all"
                    style={{ width: `${Math.round(ratio * 100)}%` }}
                  />
                </div>
              </div>
            )
          })
        )}
        {hasOverflow ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="w-full text-muted-foreground"
            onClick={() => setShowAll((expanded) => !expanded)}
          >
            {showAll ? 'Show less' : `Show all ${sortedRequirements.length}`}
          </Button>
        ) : null}
      </CardContent>
    </Card>
  )
}

export function SmartAnalysisScoreboard({
  applicants,
  coverage = null,
  summary,
  llmJudgedCount,
  metaMode = 'pipeline',
  className,
}: SmartAnalysisScoreboardProps) {
  const showCoverage = coverage != null

  return (
    <section
      aria-label="Analysis scoreboard"
      className={cn(
        'grid gap-4',
        showCoverage ? '@2card:grid-cols-2' : 'grid-cols-1',
        className,
      )}
    >
      <MatchQualityPanel
        applicants={applicants}
        summary={summary}
        llmJudgedCount={llmJudgedCount}
        metaMode={metaMode}
      />
      {showCoverage ? <RequirementCoveragePanel coverage={coverage} /> : null}
    </section>
  )
}
